'use client';
import { Library, User, Users } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useMe } from '../../lib/queries';
import { useMobileViewport } from '../../lib/useMobile';
import { usePersistentState } from '../../lib/usePersistentState';
import { Modal, ModalBody } from '../../components/ui/Modal';
import { SelectMenu } from '../../components/ui/SelectMenu';
import type { PageFilterField } from 'elowen-plugin-ui-kit';
import { useBrainChat } from './BrainChatProvider';
import { ConversationListPanel } from './ConversationListPanel';
import type { ConversationListView } from './conversationListModel';

/** THE conversation switcher: one surface for every way into the conversation list, mounted ONCE by the
 *  shell beside the chat controller it reads its open state from. The dock and /chat can be on screen
 *  together, so a per-surface switcher would be two modals over one list.
 *
 *  "Just mine" is the caller's own conversations with their snippets, activity marks, row actions and
 *  filed schedules; "All conversations" is the administrator's register. A non-administrator has no
 *  second view.
 *
 *  This half holds no state on purpose. The shell keeps it mounted for the app's whole life; what the
 *  reader chooses lives in the open modal below. The administrator's scope is remembered per browser and
 *  account like the list's other filters, so the switcher reopens on the view last chosen. */
export function ConversationSwitcherModal() {
  const { historyOpen, closeHistory } = useBrainChat();
  if (!historyOpen) return null;
  return <ConversationSwitcher onClose={closeHistory} />;
}

/** The Settings-sized centered frame, fullscreen on phones. */
function ConversationSwitcher({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.user?.is_admin === true && !me.error;
  const phone = useMobileViewport() === true;
  const [view, setView] = usePersistentState<ConversationListView>(
    `elowen.conversationList.scope.${me.data?.user?.id ?? 'anonymous'}`, 'mine', ['mine', 'all'],
  );
  // An account that loses its admin rights while the modal is open must not keep the register on screen.
  const showing: 'mine' | 'all' = isAdmin ? view : 'mine';

  const scopeOptions = [
    { value: 'mine' as const, label: t.conversationList.mine, icon: <User size={14} /> },
    { value: 'all' as const, label: t.chat.openRegister, icon: <Users size={14} /> },
  ];
  const scopeField: PageFilterField | undefined = isAdmin ? {
    id: 'scope',
    label: t.conversationList.filters.scope,
    control: <SelectMenu value={showing} onChange={setView} options={scopeOptions} label={t.conversationList.filters.scope} />,
    ...(showing === 'all'
      ? { active: true, activeLabel: t.chat.openRegister, onReset: () => setView('mine') }
      : { active: false }),
  } : undefined;

  return (
    <Modal
      title={t.chat.historyTitle}
      icon={Library}
      size="page"
      presentation={phone ? 'fullscreen' : 'center'}
      // What the register is, said only where it is on screen: the personal list needs no explanation.
      {...(showing === 'all' ? { description: t.chat.registerHint } : {})}
      onClose={onClose}
    >
      <ModalBody fill>
        {/* Both views open the chosen conversation on the full /chat page, so both dismiss this modal —
            the reader lands in what they opened rather than back on the list covering it. */}
        <ConversationListPanel key={showing} view={showing} onNavigate={onClose} homeLink={phone} scopeField={scopeField} />
      </ModalBody>
    </Modal>
  );
}
